import { preserveAnonymiser } from "@thalamos/common";

import { defaultAnonymiser } from "../../anonymisation/defaultAnonymimser.js";
import {
  combineIncidentAnonymisers,
  type FormAnonymiser,
  type IncidentWorkItemAnonymiser,
  type SectionAnonymiser,
} from "../../anonymisation/IncidentWorkItemAnonymiser.js";
import { locationFuzzerAnonymiser } from "../../anonymisation/util.js";
import { FormSectionSchema } from "../../types/FormSection.js";
import { IncidentFormTypeSchema } from "../../types/incidentWorkItem.js";
import type { LocationUnbranded, Value } from "../../types/index.js";
import type { ArrivePlaceOfSafetyFormData } from "./arrivePlaceOfSafety.js";
import type { CreateIncidentWorkItemFormData } from "./createIncident.js";
import type { DetentionDetailsFormData } from "./detentionDetails.js";
import type { FinishFormData } from "./finishForm.js";
import type { InformationForHealthcareFormData } from "./informationForHealthcare.js";
import type { LeavePlaceOfSafetyFormData } from "./leavePlaceOfSafety.js";
import type { McaCapacityAssessmentFormData } from "./mcaCapacityAssessment.js";
import type { McaIncidentDetailsFormData } from "./mcaIncidentDetails.js";
import type { McaPersonDetailsFormData } from "./mcaPersonDetails.js";
import type { PersonDetailsFormData } from "./personDetails.js";
import type { PreEndIncidentFormData } from "./preEndIncident.js";
import type { RecordPlaceOfSafetyFormData } from "./recordPlaceOfSafety.js";
import type { TriageIncidentFormData } from "./triageIncident.js";

/**
 * Sections persisted under an earlier field set can still carry an
 * `incidentLocation`. Only fields named in `fieldAnonymisers` survive
 * anonymisation, so naming it here fuzzes those coordinates instead of
 * dropping them.
 */
const createIncidentMainAnonymiser: SectionAnonymiser<
  CreateIncidentWorkItemFormData & {
    incidentLocation: Value<LocationUnbranded>;
  }
> = {
  sectionId: FormSectionSchema.enum.createIncidentMain,
  fieldAnonymisers: {
    dispatchNumberCreated: preserveAnonymiser,
    geographicUnit: preserveAnonymiser,
    userIsAttendingOfficer: preserveAnonymiser,
    createdOnBehalfOfAnotherOfficerWithShoulderNumber: preserveAnonymiser,
    incidentLocation: locationFuzzerAnonymiser,
  },
};

const createIncidentAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.createIncident,
  sectionAnonymisers: [createIncidentMainAnonymiser],
};

const triageIncidentMainAnonymiser: SectionAnonymiser<TriageIncidentFormData> =
  {
    sectionId: FormSectionSchema.enum.triageMain,
    fieldAnonymisers: {
      reasonNotConsulted: preserveAnonymiser,
      howDidYouContactNhs: preserveAnonymiser,
      givenHealthCareProfessionalAdvice: preserveAnonymiser,
      voluntaryAction: preserveAnonymiser,
      explainWhyActionDiffers: preserveAnonymiser,
      confirmMentalCapacityLack: preserveAnonymiser,
    },
  };

export const triageIncidentAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.triage,
  sectionAnonymisers: [triageIncidentMainAnonymiser],
};

const personDetailstMainAnonymiser: SectionAnonymiser<PersonDetailsFormData> = {
  sectionId: FormSectionSchema.enum.personDetails,
  fieldAnonymisers: {
    gender: preserveAnonymiser,
    ethnicAppearance: preserveAnonymiser,
    selfDeclaredEthnicity: preserveAnonymiser,
    relativeOrFriendInformed: preserveAnonymiser,
    relativeOrFriendSameAddress: preserveAnonymiser,
  },
};

const detentionDetailsAnonymiser: SectionAnonymiser<DetentionDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.detentionDetails,
    fieldAnonymisers: {
      locationType: preserveAnonymiser,
      orgResponsibleForDetention: preserveAnonymiser,
      locationDetentionTookPlace: locationFuzzerAnonymiser,
      custodyNumber: preserveAnonymiser,
      policeForceResponsibleForCustodySuite: preserveAnonymiser,
      incidentAndLocationSameLocation: preserveAnonymiser,
      incidentLocation: locationFuzzerAnonymiser,
      commandUnit: preserveAnonymiser,
      locationTypeIncident: preserveAnonymiser,
      // custodySuiteName is free text, which can hold whatever the officer
      // typed, so it must not survive.
    },
  };

const informationForHealthcareAnonymiser: SectionAnonymiser<InformationForHealthcareFormData> =
  {
    sectionId: FormSectionSchema.enum.informationForHealthcare,
    fieldAnonymisers: {
      wasHealthcareProvided: preserveAnonymiser,
      hasPersonBeenSearched: preserveAnonymiser,
      hasAdditionalRiskFactors: preserveAnonymiser,
      wasForceUsed: preserveAnonymiser,
      didSeizeProperty: preserveAnonymiser,
      describeRiskFactors: preserveAnonymiser,
      useOfForceType: preserveAnonymiser,
      useOfForceContextBaton: preserveAnonymiser,
      useOfForceContextTacticalCommunication: preserveAnonymiser,
      useOfForceContextIrritantSpray: preserveAnonymiser,
      useOfForceContextDogs: preserveAnonymiser,
      useOfForceContextDogBite: preserveAnonymiser,
      useOfForceContextGroundRestraint: preserveAnonymiser,
      useOfForceContextHandcuffingNonCompliant: preserveAnonymiser,
      useOfForceContextHandcuffingCompliant: preserveAnonymiser,
      useOfForceContextLimbBodyRestraints: preserveAnonymiser,
      useOfForceContextShield: preserveAnonymiser,
      useOfForceContextSpitGuard: preserveAnonymiser,
      useOfForceContextCed: preserveAnonymiser,
      useOfForceContextUnarmedSkills: preserveAnonymiser,
      useOfForceContextOtherImprovised: preserveAnonymiser,
      // Each context question has a free-text sibling for "other", which can
      // hold whatever the officer typed, so those must not survive.
    },
  };

export const section136Anonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.section136,
  sectionAnonymisers: [
    personDetailstMainAnonymiser,
    detentionDetailsAnonymiser,
    informationForHealthcareAnonymiser,
  ],
};

const recordPlaceOfSafetyMainAnonymiser: SectionAnonymiser<RecordPlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyMain,
    fieldAnonymisers: {
      placeOfSafetyType: preserveAnonymiser,
      whyNotHbPosReason: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyArriveAnonymiser: SectionAnonymiser<ArrivePlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    fieldAnonymisers: {
      wasEntryDelayed: preserveAnonymiser,
      howLongWasEntryDelayed: preserveAnonymiser,
      methodOfTransport: preserveAnonymiser,
      whyMethodOfTransportPoliceVehicle: preserveAnonymiser,
      whyWasEntryDelayed: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyLeaveAnonymiser: SectionAnonymiser<LeavePlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyLeave,
    fieldAnonymisers: {
      leaveReason: preserveAnonymiser,
      leaveAuthorisedBy: preserveAnonymiser,
    },
  };

export const recordPlaceOfSafetyAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.recordPlaceOfSafety,
  sectionAnonymisers: [
    recordPlaceOfSafetyMainAnonymiser,
    recordPlaceOfSafetyArriveAnonymiser,
    recordPlaceOfSafetyLeaveAnonymiser,
  ],
};

const finishFormAnonymiser: SectionAnonymiser<FinishFormData> = {
  sectionId: FormSectionSchema.enum.finishForm,
  fieldAnonymisers: {
    whyNotSharing: preserveAnonymiser,
    wasPersonAssessed: preserveAnonymiser,
    assessmentOutcomeKnown: preserveAnonymiser,
    whatWasTheOutcome: preserveAnonymiser,
    typeOfFurtherCareReceived: preserveAnonymiser,
  },
};

export const finishFormDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.finishForm,
  sectionAnonymisers: [finishFormAnonymiser],
};

const preEndIncidentAnonymiser: SectionAnonymiser<PreEndIncidentFormData> = {
  sectionId: FormSectionSchema.enum.preEndIncident,
  fieldAnonymisers: {
    handoverOccur: preserveAnonymiser,
    leavePos: preserveAnonymiser,
    whyNhsNotEngage: preserveAnonymiser,
    whyEndingMcaForm: preserveAnonymiser,
    whyFormExpired: preserveAnonymiser,
    furtherCareType: preserveAnonymiser,
    outcomeOfAssessment: preserveAnonymiser,
    wasAssessedMentalHealthPlaceOfSafety: preserveAnonymiser,
    didHandoverOccur: preserveAnonymiser,
    selectedAssessmentOutcome: preserveAnonymiser,
    endingReason: preserveAnonymiser,
  },
};

export const preEndIncidentFormAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.preEndIncident,
  sectionAnonymisers: [preEndIncidentAnonymiser],
};

const mcaPersonDetailsAnonymiser: SectionAnonymiser<McaPersonDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaPersonDetails,
    fieldAnonymisers: {
      gender: preserveAnonymiser,
      selfDeclaredEthnicity: preserveAnonymiser,
      relativeOrFriendSameAddress: preserveAnonymiser,
      relativeOrFriendInformed: preserveAnonymiser,
    },
  };

const mcaIncidentDetailsAnonymiser: SectionAnonymiser<McaIncidentDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaIncidentDetails,
    fieldAnonymisers: {
      bcuIncidentTookPlace: preserveAnonymiser,
      locationType: preserveAnonymiser,
      incidentLocation: locationFuzzerAnonymiser,
      custodyNumber: preserveAnonymiser,
      policeForceResponsibleForCustodySuite: preserveAnonymiser,
      // A field named here survives anonymisation; anything unnamed is dropped.
      // custodySuiteName is free text, which can hold whatever the officer
      // typed, so it must not survive.
    },
  };

const mcaCapacityAssessmentAnonymiser: SectionAnonymiser<McaCapacityAssessmentFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaCapacityAssessment,
    fieldAnonymisers: {
      assessingIndividual: preserveAnonymiser,
      bestInterestPhysicalHealthCondition: preserveAnonymiser,
      useOfForceType: preserveAnonymiser,
      earlyExitCapable: preserveAnonymiser,
      earlyExitNoImpairment: preserveAnonymiser,
      earlyExitNoLackOfCapacity: preserveAnonymiser,
    },
  };

const mentalCapacityActAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.mentalCapacityAct,
  sectionAnonymisers: [
    mcaPersonDetailsAnonymiser,
    mcaIncidentDetailsAnonymiser,
    mcaCapacityAssessmentAnonymiser,
  ],
};

const btpSpecificAnonymiser: IncidentWorkItemAnonymiser = {
  coreIncidentAnonymiser: (incidentWorkItem) => incidentWorkItem,

  formAnonymisers: [
    createIncidentAnonymiser,
    triageIncidentAnonymiser,
    section136Anonymiser,
    mentalCapacityActAnonymiser,

    recordPlaceOfSafetyAnonymiser,

    finishFormDefaultAnonymiser,
    preEndIncidentFormAnonymiser,
  ],
};

export const btpAnonymiser = combineIncidentAnonymisers(
  defaultAnonymiser,
  btpSpecificAnonymiser,
);
